// AttachmentComposer — paperclip button + per-attachment chips for the
// chat composer. Handles upload, status tracking, object-URL lifecycle,
// and exposes the list of ready attachment ids upward.
//
// Design constraints (from add-chat-attachments spec):
//  - Max 5 attachments per message; button is disabled beyond that cap.
//  - Upload is fire-and-forget per file; status chips reflect the live state.
//  - Image chips show a thumbnail from `URL.createObjectURL`; doc chips show
//    a FileText icon + truncated filename.
//  - An image chip's thumbnail + filename are ONE button that opens the app's
//    shared `ImageLightbox` (`unify-image-lightbox`) over the composer's image
//    attachments, in chip order. The composer — not the chip — owns which one
//    is open, so previous/next, the arrow keys and the `n de N` indicator walk
//    the set without closing the dialog. The slide reads the chip's local
//    `objectUrl`, so any upload status can be previewed (a file that is still
//    uploading or failed is still a file the user picked). Document chips stay
//    non-interactive, and the status indicator and the remove `X` stay separate
//    siblings of the preview button (no nested buttons): removing never opens
//    the lightbox.
//  - Remove calls DELETE when an attachmentId is present; always revokes
//    the objectURL.
//  - `onReadyIdsChange` is called whenever the ready-id list changes so the
//    parent can gate Send and collect the ids to forward to `streamChat`.

import { Button } from "@/components/ui/button";
import { ImageLightbox, type LightboxSlide } from "@/components/ui/image-lightbox";
import { deleteAttachment, uploadAttachment } from "@/lib/chat";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, Paperclip, X } from "lucide-react";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_ATTACHMENTS = 5;

/** MIME types + extensions accepted by v1 of the attachment pipeline.
 * Extensions are included alongside MIME types because some browsers (notably
 * Safari on iOS/macOS) do not recognise non-standard MIME types such as
 * image/heic and image/heif in the file picker, so the explicit extensions
 * ensure those formats surface correctly. */
const ACCEPTED_MIME_TYPES = [
  // Images — MIME types
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/tiff",
  "image/bmp",
  "image/heic",
  "image/heif",
  // Images — explicit extensions for browsers that ignore non-standard MIMEs
  ".heic",
  ".heif",
  ".tif",
  ".tiff",
  ".bmp",
  // Documents — MIME types
  "text/markdown",
  "text/plain",
  "text/csv",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  // Documents — explicit extensions (a browser may declare a CSV as another type)
  ".md",
  ".markdown",
  ".csv",
].join(",");

// ---------------------------------------------------------------------------
// Clipboard helper
// ---------------------------------------------------------------------------

/** Return the files carried by a clipboard paste, or `null` when the paste
 * carries none (e.g. a plain-text paste). Lets the chat composer route pasted
 * screenshots / copied files through the same upload path as the paperclip
 * button while leaving text pastes untouched. */
export function filesFromClipboard(data: DataTransfer | null): FileList | null {
  if (data && data.files.length > 0) return data.files;
  return null;
}

// ---------------------------------------------------------------------------
// Local attachment state
// ---------------------------------------------------------------------------

type AttachmentLocalStatus = "uploading" | "ready" | "failed";

export interface LocalAttachment {
  /** Client-side uuid — used as React key and for mutations before the
   * server id is known. */
  localId: string;
  file: File;
  objectUrl: string;
  attachmentId?: string;
  kind?: "image" | "document";
  status: AttachmentLocalStatus;
  error?: string;
}

/** The rule the chip uses to decide it is an image — the server-declared kind
 * once the upload answered, the browser's MIME type before that. The lightbox
 * set is built with the same rule so a chip with a preview button is always in
 * the set and a document never is. */
function isImageAttachment(attachment: LocalAttachment): boolean {
  return (
    attachment.kind === "image" ||
    (attachment.kind === undefined && attachment.file.type.startsWith("image/"))
  );
}

// ---------------------------------------------------------------------------
// Ready attachment shape (exported so chat.tsx can map to ChatMessageAttachment)
// ---------------------------------------------------------------------------

/** Minimal descriptor for each attachment that has completed upload successfully.
 * Satisfies `ChatMessageAttachment` — same fields, `status` fixed to "ready". */
export interface ReadyAttachment {
  id: string;
  filename: string;
  kind: "image" | "document";
  status: "ready";
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AttachmentComposerProps {
  /** Current chat session id (may be undefined before first message). */
  sessionId: string | undefined;
  /** Called whenever the set of ready attachments changes.
   * Parent derives ids for `streamChat` and stores full objects for the live
   * user turn so `<AttachmentList>` renders immediately after send. */
  onReadyAttachmentsChange: (attachments: ReadyAttachment[]) => void;
  /** Called whenever the "any uploading or failed" gate changes.
   * Parent disables Send while this is true. */
  onBlockingChange: (blocked: boolean) => void;
  /** Imperative handle to reset all attachments after a successful send. */
  resetRef: RefObject<(() => void) | null>;
  /** Imperative handle exposing the file-intake path so the parent can feed
   * files from other sources (e.g. a clipboard paste in the chat textarea)
   * through the same upload pipeline as the paperclip button. */
  addFilesRef?: RefObject<((files: FileList) => void) | null>;
  /** When true, the add-attachment button is disabled (e.g. during send). */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AttachmentComposer({
  sessionId,
  onReadyAttachmentsChange,
  onBlockingChange,
  resetRef,
  addFilesRef,
  disabled = false,
}: AttachmentComposerProps) {
  const [attachments, setAttachments] = useState<LocalAttachment[]>([]);
  // The list the composer holds RIGHT NOW. Every change goes through
  // `updateAttachments`, which advances this ref synchronously and hands React
  // the finished list, so a handler that runs before React has rendered the last
  // change still decides on the latest one. That is what lets two intakes in the
  // same tick see each other (the cap holds) and lets an intake start uploads for
  // exactly the files it admitted without relying on when React evaluates a state
  // updater (React only evaluates one eagerly when no update is pending).
  const attachmentsRef = useRef<LocalAttachment[]>([]);
  // `localId` of the open IMAGE attachment; `null` = lightbox closed.
  const [lightboxKey, setLightboxKey] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const updateAttachments = useCallback(
    (change: (current: LocalAttachment[]) => LocalAttachment[]) => {
      const next = change(attachmentsRef.current);
      attachmentsRef.current = next;
      setAttachments(next);
    },
    [],
  );

  // Expose a reset handle to the parent so it can clear state after send.
  useEffect(() => {
    resetRef.current = () => {
      for (const a of attachmentsRef.current) URL.revokeObjectURL(a.objectUrl);
      updateAttachments(() => []);
    };
    return () => {
      resetRef.current = null;
    };
  }, [resetRef, updateAttachments]);

  // Revoke all object URLs on unmount so we don't leak blob memory.
  useEffect(() => {
    return () => {
      for (const a of attachmentsRef.current) URL.revokeObjectURL(a.objectUrl);
      updateAttachments(() => []);
    };
  }, [updateAttachments]);

  // Notify parent whenever the attachment list changes.
  useEffect(() => {
    const ready: ReadyAttachment[] = attachments
      .filter(
        (
          a,
        ): a is LocalAttachment & {
          attachmentId: string;
          kind: "image" | "document";
        } => a.status === "ready" && a.attachmentId !== undefined && a.kind !== undefined,
      )
      .map((a) => ({
        id: a.attachmentId,
        filename: a.file.name,
        kind: a.kind,
        status: "ready" as const,
      }));
    onReadyAttachmentsChange(ready);

    const blocked = attachments.some((a) => a.status === "uploading" || a.status === "failed");
    onBlockingChange(blocked);
  }, [attachments, onReadyAttachmentsChange, onBlockingChange]);

  // ---------------------------------------------------------------------------
  // Upload mutation
  // ---------------------------------------------------------------------------

  const uploadMutation = useMutation({
    mutationFn: ({ file, sid }: { file: File; sid: string | undefined }) =>
      uploadAttachment(file, sid),
    onSuccess: (data, variables) => {
      updateAttachments((current) =>
        current.map((a) =>
          a.file === variables.file
            ? {
                ...a,
                attachmentId: data.attachment_id,
                kind: data.kind,
                status: data.status === "failed" ? "failed" : "ready",
                ...(data.error ? { error: data.error } : {}),
              }
            : a,
        ),
      );
    },
    onError: (err, variables) => {
      const errorMsg = err instanceof Error ? err.message : "upload failed";
      updateAttachments((current) =>
        current.map((a) =>
          a.file === variables.file ? { ...a, status: "failed", error: errorMsg } : a,
        ),
      );
    },
  });

  // ---------------------------------------------------------------------------
  // Delete mutation
  // ---------------------------------------------------------------------------

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: string) => deleteAttachment(attachmentId),
    onSettled: () => {
      // Invalidate if there's ever a query that lists attachments; harmless
      // to invalidate when there isn't one.
      void queryClient.invalidateQueries({ queryKey: ["chat", "attachments"] });
    },
  });

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleFilesSelected = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      // Build candidate LocalAttachments eagerly so we have object URLs and
      // localIds ready. At most MAX_ATTACHMENTS are created here; which of them
      // fit is decided right below from the list the composer holds now (the ref
      // already includes files added a moment earlier, even if React has not
      // rendered them yet), so two rapid calls cannot both read a stale length
      // and bypass the cap.
      const candidates: LocalAttachment[] = Array.from(files)
        .slice(0, MAX_ATTACHMENTS)
        .map((file) => ({
          localId: crypto.randomUUID(),
          file,
          objectUrl: URL.createObjectURL(file),
          status: "uploading" as const,
        }));

      const remaining = Math.max(MAX_ATTACHMENTS - attachmentsRef.current.length, 0);
      const admitted = candidates.slice(0, remaining);
      // Revoke the object URLs of candidates that did not fit.
      for (const c of candidates.slice(remaining)) URL.revokeObjectURL(c.objectUrl);

      // One upload per admitted file, started here and nowhere else: not inside a
      // state updater (React may evaluate it later, or not eagerly at all).
      if (admitted.length > 0) {
        updateAttachments((current) => [...current, ...admitted]);
        for (const a of admitted) {
          uploadMutation.mutate({ file: a.file, sid: sessionId });
        }
      }

      // Reset input so the same file can be re-selected after removal.
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [sessionId, uploadMutation, updateAttachments],
  );

  const handleRemove = useCallback(
    (localId: string) => {
      const target = attachmentsRef.current.find((a) => a.localId === localId);
      if (!target) return;

      URL.revokeObjectURL(target.objectUrl);

      if (target.attachmentId) {
        deleteMutation.mutate(target.attachmentId);
      }

      updateAttachments((current) => current.filter((a) => a.localId !== localId));
    },
    [deleteMutation, updateAttachments],
  );

  // Expose the file-intake handler so the parent can forward files from other
  // sources (clipboard paste) through the same upload path. Mirrors `resetRef`.
  useEffect(() => {
    if (!addFilesRef) return;
    addFilesRef.current = handleFilesSelected;
    return () => {
      addFilesRef.current = null;
    };
  }, [addFilesRef, handleFilesSelected]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const atCap = attachments.length >= MAX_ATTACHMENTS;

  // A fresh array every render is fine: the lightbox is controlled and reads
  // only what it is handed. If the open image vanishes (a reset after send) the
  // lightbox asks to close through `onClose`.
  const imageAttachments = attachments.filter(isImageAttachment);
  const slides: LightboxSlide[] = imageAttachments.map((a) => ({
    key: a.localId,
    alt: a.file.name,
    heading: a.file.name,
    image: { status: "ready", url: a.objectUrl },
  }));

  return (
    <div className="flex flex-wrap items-center gap-1">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_MIME_TYPES}
        className="sr-only"
        aria-label="Seleccionar archivos"
        tabIndex={-1}
        onChange={(e) => handleFilesSelected(e.target.files)}
        data-testid="attachment-file-input"
      />

      {/* Paperclip button */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="!px-2"
        disabled={disabled || atCap}
        aria-label="Adjuntar archivo"
        title={atCap ? `Máximo ${MAX_ATTACHMENTS} archivos` : "Adjuntar archivo"}
        onClick={() => fileInputRef.current?.click()}
        data-testid="attachment-button"
      >
        <Paperclip className="h-5 w-5" />
      </Button>

      {/* Per-attachment chips */}
      {attachments.map((a) => (
        <AttachmentChip
          key={a.localId}
          attachment={a}
          onRemove={handleRemove}
          onPreview={setLightboxKey}
        />
      ))}

      <ImageLightbox
        slides={slides}
        activeKey={lightboxKey}
        onActiveKeyChange={setLightboxKey}
        onClose={() => setLightboxKey(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AttachmentChip
// ---------------------------------------------------------------------------

function AttachmentChip({
  attachment,
  onRemove,
  onPreview,
}: {
  attachment: LocalAttachment;
  onRemove: (localId: string) => void;
  onPreview: (localId: string) => void;
}) {
  const isImage = isImageAttachment(attachment);

  const statusRing: Record<AttachmentLocalStatus, string> = {
    uploading: "border-border opacity-60",
    ready: "border-brand-extra-2-40",
    failed: "border-destructive",
  };

  return (
    <div
      className={`relative flex max-w-[9rem] items-center gap-1 rounded-md border px-1.5 py-1 text-body-3 ${statusRing[attachment.status]}`}
      data-testid={`attachment-chip-${attachment.localId}`}
      title={attachment.error ?? attachment.file.name}
    >
      {/* Image: thumbnail + filename are ONE preview button. Document: icon +
          filename, not interactive. */}
      {isImage ? (
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Ver imagen: ${attachment.file.name}`}
          onClick={() => onPreview(attachment.localId)}
          data-testid={`attachment-preview-${attachment.localId}`}
        >
          <img
            src={attachment.objectUrl}
            alt={attachment.file.name}
            className="h-6 w-6 shrink-0 rounded object-cover"
          />
          <span className="min-w-0 flex-1 truncate text-foreground">{attachment.file.name}</span>
        </button>
      ) : (
        <>
          <FileText className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-foreground">{attachment.file.name}</span>
        </>
      )}

      {/* Status indicator */}
      {attachment.status === "uploading" && (
        <span className="ml-0.5 shrink-0 text-muted-foreground" aria-label="subiendo">
          …
        </span>
      )}
      {attachment.status === "failed" && (
        <span
          className="ml-0.5 shrink-0 text-destructive"
          aria-label={`error: ${attachment.error ?? "upload failed"}`}
          title={attachment.error ?? "upload failed"}
        >
          !
        </span>
      )}

      {/* Remove button */}
      <button
        type="button"
        className="ml-0.5 shrink-0 rounded p-px text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label={`Quitar ${attachment.file.name}`}
        onClick={() => onRemove(attachment.localId)}
        data-testid={`attachment-remove-${attachment.localId}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
